/**
 * Read the duration (seconds) of a video file on the client before upload.
 * Used to drive per-second pricing display for motion-control style models —
 * before this exists the price label can't reflect what the user actually
 * uploaded and just shows the model's base price.
 *
 * Implementation: spin up an off-DOM <video> element pointed at a blob URL,
 * wait for the metadata event, read the duration, revoke the URL. Resolves
 * to null when the browser refuses to load the file (unsupported codec,
 * corrupted file) so callers can fall back to the base price gracefully.
 */
export function getVideoDurationFromFile(file: File): Promise<number | null> {
  if (typeof window === 'undefined' || typeof URL === 'undefined') {
    return Promise.resolve(null);
  }

  return new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const video = document.createElement('video');
    video.preload = 'metadata';
    video.muted = true;
    let settled = false;
    const cleanup = (value: number | null) => {
      if (settled) return;
      settled = true;
      URL.revokeObjectURL(url);
      resolve(value);
    };
    video.onloadedmetadata = () => {
      const d = video.duration;
      cleanup(Number.isFinite(d) && d > 0 ? d : null);
    };
    video.onerror = () => cleanup(null);
    // Hard timeout — some mobile browsers stall forever on weird files.
    window.setTimeout(() => cleanup(null), 5000);
    video.src = url;
  });
}
